const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

class MCPToolService {
  /**
   * Fetch all MCP tools
   * @param {string} token - Authentication token
   * @param {Object} filters - Optional filters
   * @returns {Promise<Array>} List of tools
   */
  static async getTools(token, filters = {}) {
    // MOCK: Empty list to start
    // When backend is ready, uncomment:
    
    const params = new URLSearchParams();
    
    if (filters.search) {
      params.append('search', filters.search);
    }
    
    if (filters.enabled !== undefined) {
      params.append('enabled', filters.enabled);
    }
    
    const url = `${API_BASE_URL}/mcp-tools?${params.toString()}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data || [];
    

    // MOCK DATA - Returns empty list
    await new Promise(resolve => setTimeout(resolve, 500)); // Simulates API delay
    return [];
  }

  /**
   * Create new MCP tool
   * @param {Object} toolData - Tool data
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} Created tool
   */
  static async createTool(toolData, token) {
    // MOCK: Simulates successful creation
    // When backend is ready, uncomment:
    
    const response = await fetch(`${API_BASE_URL}/mcp-tools`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(toolData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Update MCP tool
   * @param {string} toolId - Tool ID
   * @param {Object} updateData - Update data
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} Updated tool
   */
  static async updateTool(toolId, updateData, token) {
    // MOCK: Simulates successful update
    // When backend is ready, uncomment:
    
    const response = await fetch(`${API_BASE_URL}/mcp-tools/${toolId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Delete MCP tool
   * @param {string} toolId - Tool ID
   * @param {string} token - Authentication token
   * @returns {Promise<boolean>} Operation success
   */
  static async deleteTool(toolId, token) {
    // MOCK: Simulates successful deletion
    // When backend is ready, uncomment:
    
    const response = await fetch(`${API_BASE_URL}/mcp-tools/${toolId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }

    return true;
  }

  /**
   * Search MCP tool by ID
   * @param {string} toolId - Tool ID
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} Found tool
   */
  static async getToolById(toolId, token) {
    // MOCK: Simulates successful search
    // When backend is ready, uncomment:
    
    const response = await fetch(`${API_BASE_URL}/mcp-tools/${toolId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Test connection with MCP Server
   * @param {string} mcpServerUrl - MCP Server URL
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} Test result
   */
  static async testMCPConnection(mcpServerUrl, token) {
    // MOCK: Simulates connection test
    // When backend is ready, implement real validation
    
    const response = await fetch(`${API_BASE_URL}/mcp-tools/test-connection`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ mcp_server_url: mcpServerUrl }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  }
}

export default MCPToolService;